import React, { useState } from "react";
import { ArrowLeft, LogOut, Shield, Gem, Crown } from "lucide-react";
import { InlineIcon } from "./Icon";
import { API_BASE } from "../config/appConfig";

const TIER_META = {
  basic: {
    label: "Basic",
    Icon: Shield,
    bg: "var(--color-bg-subtle)",
    color: "var(--color-text-muted)",
    border: "var(--color-border)",
  },
  premium: {
    label: "Premium",
    Icon: Gem,
    bg: "var(--color-primary-light)",
    color: "var(--color-primary)",
    border: "var(--color-primary-border)",
  },
  enterprise: {
    label: "Enterprise",
    Icon: Crown,
    bg: "var(--color-warning-bg)",
    color: "var(--color-warning)",
    border: "var(--color-warning-border)",
  },
};

function UsageRow({ label, used, max, atLimit }) {
  const unlimited = max === null || max === undefined;
  const pct = unlimited ? 0 : Math.min((used / max) * 100, 100);
  const barColor = atLimit ? "var(--color-danger)" : pct >= 80 ? "var(--color-warning)" : "var(--color-primary)";

  return (
    <div style={{ marginBottom: "20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
        <span style={{ color: "var(--color-text-muted)", fontSize: "var(--text-sm)" }}>
          {label}
        </span>
        <span
          style={{
            color: atLimit ? "var(--color-danger)" : "var(--color-text-primary)",
            fontSize: "var(--text-sm)",
            fontWeight: "var(--font-medium)",
          }}
        >
          {unlimited ? "Bez limitu" : `${used} / ${max}`}
        </span>
      </div>
      {!unlimited && (
        <div
          style={{
            height: "6px",
            borderRadius: "var(--radius-full)",
            background: "var(--color-bg-subtle)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${pct}%`,
              backgroundColor: barColor,
              transition: "width 0.3s ease",
            }}
          />
        </div>
      )}
    </div>
  );
}

export default function UserScreen({ auth, userTier, userLimits, onLogout, onBack }) {
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: "", new: "", confirm: "" });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");

  const key = String(userTier || "").toLowerCase();
  const meta = TIER_META[key] || TIER_META.basic;
  const TierIcon = meta.Icon;

  const eventsUsed = userLimits?.events?.used ?? 0;
  const eventsMax = userLimits?.events?.max ?? null;
  const eventsAtLimit = userLimits?.events?.at_limit === true;
  const contractorsMax = userLimits?.contractors_per_event?.max ?? null;

  const initials = auth?.username ? auth.username.slice(0, 2).toUpperCase() : "?";

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    if (!passwordForm.current.trim()) {
      setPasswordError("Obecne hasło jest wymagane.");
      return;
    }
    if (!passwordForm.new.trim()) {
      setPasswordError("Nowe hasło jest wymagane.");
      return;
    }
    if (passwordForm.new.length < 8) {
      setPasswordError("Nowe hasło musi mieć co najmniej 8 znaków.");
      return;
    }
    if (passwordForm.new !== passwordForm.confirm) {
      setPasswordError("Hasła się nie zgadzają.");
      return;
    }

    setPasswordLoading(true);
    try {
      const response = await fetch(`${API_BASE}/auth/users/set_password/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `JWT ${auth?.access}`,
        },
        body: JSON.stringify({
          current_password: passwordForm.current,
          new_password: passwordForm.new,
          re_new_password: passwordForm.confirm,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const errorMsg =
          data.current_password?.[0] ||
          data.new_password?.[0] ||
          data.non_field_errors?.[0] ||
          data.detail ||
          "Nie udało się zmienić hasła.";
        setPasswordError(errorMsg);
        return;
      }

      setPasswordSuccess("Hasło zostało zmienione.");
      setPasswordForm({ current: "", new: "", confirm: "" });
      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordSuccess("");
      }, 2000);
    } catch (err) {
      setPasswordError("Błąd sieciowy. Spróbuj ponownie.");
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm("Na pewno chcesz się wylogować?")) {
      onLogout();
    }
  };

  return (
    <>
      {/* Header */}
      <div
        style={{
          background: "var(--color-bg-card)",
          borderBottom: "1px solid var(--color-border)",
          padding: "20px",
          paddingTop: "calc(20px + env(safe-area-inset-top))",
          display: "flex",
          alignItems: "center",
          gap: "12px",
        }}
      >
        <button
          onClick={onBack}
          style={{
            background: "none",
            border: "none",
            color: "var(--color-text-primary)",
            cursor: "pointer",
            padding: "8px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <InlineIcon Icon={ArrowLeft} />
        </button>
        <h1 style={{ margin: 0, fontSize: "var(--text-h2)", fontWeight: "var(--font-semibold)" }}>
          Profil
        </h1>
      </div>

      {/* Content */}
      <div
        style={{
          padding: "20px",
          maxWidth: "600px",
          margin: "0 auto",
          overflowY: "auto",
          maxHeight: "calc(100dvh - 80px)",
          paddingBottom: "40px",
        }}
      >
        {/* User card */}
        <div
          style={{
            background: "var(--color-bg-card)",
            borderRadius: "var(--radius-lg)",
            border: "1px solid var(--color-border)",
            padding: "24px",
            textAlign: "center",
            marginBottom: "16px",
          }}
        >
          {/* Avatar */}
          <div
            style={{
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              backgroundColor: meta.bg,
              borderWidth: "2px",
              borderStyle: "solid",
              borderColor: meta.border,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
              fontSize: "28px",
              fontWeight: "var(--font-bold)",
              color: meta.color,
            }}
          >
            {initials}
          </div>

          {/* Username */}
          <div style={{ fontSize: "var(--text-h3)", fontWeight: "var(--font-semibold)" }}>
            {auth?.username ?? "Użytkownik"}
          </div>
        </div>

        {/* Limits card */}
        <div
          style={{
            background: "var(--color-bg-card)",
            borderRadius: "var(--radius-lg)",
            border: "1px solid var(--color-border)",
            padding: "20px",
            marginBottom: "16px",
          }}
        >
          <h2
            style={{
              fontSize: "var(--text-h3)",
              fontWeight: "var(--font-semibold)",
              marginTop: 0,
              marginBottom: "16px",
            }}
          >
            Twoje limity
          </h2>

          {userLimits ? (
            <>
              <UsageRow
                label="Wydarzenia"
                used={eventsUsed}
                max={eventsMax}
                atLimit={eventsAtLimit}
              />
              <div style={{ marginBottom: "20px" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "var(--color-text-muted)", fontSize: "var(--text-sm)" }}>
                    Wykonawcy na wydarzenie
                  </span>
                  <span style={{ color: "var(--color-text-primary)", fontSize: "var(--text-sm)", fontWeight: "var(--font-medium)" }}>
                    {contractorsMax === null ? "Bez limitu" : `max ${contractorsMax}`}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <div style={{ color: "var(--color-text-muted)" }}>Ładowanie danych...</div>
          )}

          <div
            style={{
              height: "1px",
              background: "var(--color-border)",
              margin: "16px 0",
            }}
          />

          <div style={{ color: "var(--color-text-muted)", fontSize: "var(--text-xs)" }}>
            Aby zmienić plan, skontaktuj się z administratorem.
          </div>
        </div>

        {/* Change password button */}
        <button
          onClick={() => setShowPasswordModal(true)}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            padding: "12px 16px",
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--color-border)",
            background: "var(--color-bg-card)",
            color: "var(--color-text-primary)",
            fontSize: "var(--text-body)",
            fontWeight: "var(--font-medium)",
            cursor: "pointer",
            fontFamily: "inherit",
            marginBottom: "12px",
          }}
        >
          <Shield size={16} />
          <span>Zmień hasło</span>
        </button>

        {/* Logout button */}
        <button
          onClick={handleLogout}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            padding: "12px 16px",
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--color-danger)",
            background: "var(--color-danger-light)",
            color: "var(--color-danger)",
            fontSize: "var(--text-body)",
            fontWeight: "var(--font-medium)",
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          <LogOut size={16} />
          <span>Wyloguj się</span>
        </button>

        {/* Password change modal */}
        {showPasswordModal && (
          <div
            onClick={() => !passwordLoading && setShowPasswordModal(false)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.35)",
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
              boxSizing: "border-box",
            }}
          >
            <form
              onSubmit={handleChangePassword}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "100%",
                maxWidth: 420,
                background: "var(--color-bg-card)",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-lg)",
                padding: 24,
              }}
            >
              <h2
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: "var(--text-h3)",
                  fontWeight: "var(--font-semibold)",
                  color: "var(--color-text-primary)",
                  marginTop: 0,
                  marginBottom: 16,
                }}
              >
                Zmień hasło
              </h2>

              {passwordError && (
                <div
                  style={{
                    background: "var(--color-danger-bg)",
                    border: "1px solid color-mix(in srgb, var(--color-danger) 28%, white)",
                    color: "var(--color-danger)",
                    borderRadius: "var(--radius-md)",
                    padding: "10px 12px",
                    marginBottom: 16,
                    fontSize: "var(--text-sm)",
                  }}
                >
                  {passwordError}
                </div>
              )}

              {passwordSuccess && (
                <div
                  style={{
                    background: "var(--color-success-light)",
                    border: "1px solid color-mix(in srgb, var(--color-success) 28%, white)",
                    color: "var(--color-success)",
                    borderRadius: "var(--radius-md)",
                    padding: "10px 12px",
                    marginBottom: 16,
                    fontSize: "var(--text-sm)",
                  }}
                >
                  {passwordSuccess}
                </div>
              )}

              <label style={{ display: "block", marginBottom: 16 }}>
                <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 6 }}>
                  Obecne hasło
                </div>
                <input
                  type="password"
                  value={passwordForm.current}
                  onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
                  disabled={passwordLoading}
                  style={{
                    width: "100%",
                    border: "1px solid var(--color-border)",
                    borderRadius: "var(--radius-md)",
                    padding: "10px 12px",
                    fontSize: "var(--text-body)",
                    boxSizing: "border-box",
                    opacity: passwordLoading ? 0.6 : 1,
                  }}
                />
              </label>

              <label style={{ display: "block", marginBottom: 16 }}>
                <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 6 }}>
                  Nowe hasło
                </div>
                <input
                  type="password"
                  value={passwordForm.new}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new: e.target.value })}
                  disabled={passwordLoading}
                  style={{
                    width: "100%",
                    border: "1px solid var(--color-border)",
                    borderRadius: "var(--radius-md)",
                    padding: "10px 12px",
                    fontSize: "var(--text-body)",
                    boxSizing: "border-box",
                    opacity: passwordLoading ? 0.6 : 1,
                  }}
                />
              </label>

              <label style={{ display: "block", marginBottom: 20 }}>
                <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 6 }}>
                  Potwierdź nowe hasło
                </div>
                <input
                  type="password"
                  value={passwordForm.confirm}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                  disabled={passwordLoading}
                  style={{
                    width: "100%",
                    border: "1px solid var(--color-border)",
                    borderRadius: "var(--radius-md)",
                    padding: "10px 12px",
                    fontSize: "var(--text-body)",
                    boxSizing: "border-box",
                    opacity: passwordLoading ? 0.6 : 1,
                  }}
                />
              </label>

              <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 16 }}>
                Nowe hasło musi mieć co najmniej 8 znaków.
              </div>

              <div style={{ display: "flex", gap: 12 }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordForm({ current: "", new: "", confirm: "" });
                    setPasswordError("");
                    setPasswordSuccess("");
                  }}
                  disabled={passwordLoading}
                  style={{
                    flex: 1,
                    padding: "12px 16px",
                    borderRadius: "var(--radius-md)",
                    border: "1px solid var(--color-border)",
                    background: "var(--color-bg-subtle)",
                    color: "var(--color-text-primary)",
                    fontSize: "var(--text-body)",
                    fontWeight: "var(--font-medium)",
                    cursor: passwordLoading ? "not-allowed" : "pointer",
                    fontFamily: "inherit",
                    opacity: passwordLoading ? 0.6 : 1,
                  }}
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={passwordLoading}
                  style={{
                    flex: 1,
                    padding: "12px 16px",
                    borderRadius: "var(--radius-md)",
                    border: "none",
                    background: "var(--color-primary)",
                    color: "var(--color-bg-card)",
                    fontSize: "var(--text-body)",
                    fontWeight: "var(--font-medium)",
                    cursor: passwordLoading ? "not-allowed" : "pointer",
                    fontFamily: "inherit",
                    opacity: passwordLoading ? 0.6 : 1,
                  }}
                >
                  {passwordLoading ? "Wysyłanie..." : "Zmień hasło"}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </>
  );
}
